/**
 * Reply Queue — the drafts-truth view of unanswered inbound messages.
 *
 * `os_conversation_threads.status` is a STORED column that can go stale
 * (threads stamped `resolved` long after a new inbound message arrived,
 * some with no linked contact at all). Rather than rewriting thread status
 * history, this hook surfaces the truth directly from `os_linkedin_drafts`:
 * a lead belongs in the queue iff it has at least one live, actionable,
 * unanswered draft whose thread is not CONFIRMED Trashed (see
 * `excludeSoftDeletedThreads` — a draft with no thread at all is still a
 * valid member, rendered via the fallback-row path).
 *
 * Two hooks share the same membership logic but select different columns:
 * `useReplyQueueCount` is cheap and always mounted (it powers the tab badge
 * even when the tab isn't open), `useReplyQueue` fetches full row content
 * and should only be enabled while the tab is actually visible — nobody
 * needs 233 people's incoming message bodies in cache just to render a
 * number on a button.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { LIVE_DRAFT_STATUSES, compareDraftRecency, pickMostRecentDraft } from '@/lib/crm/live-draft';
import { excludeConfirmedTrashedThreads } from '@/lib/crm/thread-lifecycle';
import { useAuth } from '@/contexts/AuthContext';
import { readAllRowsKeyset } from '@/lib/supabase-pagination';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ReplyQueueSenderFilter = string | 'all' | 'unassigned';

/** The statuses that mean "this inbound message is still live and unanswered" —
 * shared with the Lead Dossier via src/lib/crm/live-draft.ts so the two
 * surfaces can't independently drift on which drafts count as "live". */
const REPLY_QUEUE_STATUSES = LIVE_DRAFT_STATUSES;

/** The minimum a row needs to carry to dedupe by lead, apply the archived-contact
 * and soft-deleted-thread exclusions, and — via `id` — anchor a keyset
 * pagination cursor. */
interface ReplyQueueIdentityRow {
  id: string;
  heyreach_lead_id: string;
  incoming_at: string;
  contact_id: string | null;
  thread_id: string | null;
}

export interface ReplyQueueDraftRow extends ReplyQueueIdentityRow {
  heyreach_campaign_id: string;
  lead_name: string;
  lead_title: string | null;
  lead_company: string | null;
  incoming_message: string;
}

export interface ReplyQueueFilters {
  senderId: ReplyQueueSenderFilter;
  campaignId: string | null;
  /** Default off: leads whose linked contact is archived are hidden. */
  includeArchived: boolean;
}

export interface DraftCampaign {
  heyreach_campaign_id: string;
  campaign_name: string;
}

export interface ReplyQueueResult {
  rows: ReplyQueueDraftRow[];
  /** Not `totalCount` — that duplicated `rows.length` and had no reader; the
   * badge's count comes from the separate, always-mounted useReplyQueueCount. */
  archivedExcludedCount: number;
}

export interface ReplyQueueCountResult {
  totalCount: number;
}

/**
 * What `os_conversation_inbox` supplies that a `ReplyQueueDraftRow` doesn't:
 * pipeline stage, message/pending counts, and the assigned sender's seat
 * name. Deliberately excludes lead identity and message content — those are
 * authoritative on the draft row and would otherwise be fetched twice (and,
 * per F1, silently go stale in this cache while the row itself stays fresh).
 */
export interface ReplyQueueThreadMetadata {
  thread_id: string;
  current_stage: string;
  message_count: number;
  pending_draft_count: number;
  sender_name: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PostgREST caps an unbounded select at 1,000 rows and silently truncates —
 * paginate in pages well under that cap so growth can never truncate the
 * queue without warning.
 */
const REPLY_QUEUE_PAGE_SIZE = 500;

/** Same reasoning as REPLY_QUEUE_PAGE_SIZE: comfortably under PostgREST's silent 1,000-row cap, so
 * growth can never truncate the campaign domain without warning. */
const DRAFT_CAMPAIGN_DOMAIN_PAGE_SIZE = 500;

/** Keep `.in()` lookups comfortably below any URL/query-length limits. */
const LOOKUP_CHUNK_SIZE = 50;

/**
 * The queue can change underneath a user parked in a focused Inbox tab (a new
 * inbound arrives, a draft gets sent/rejected elsewhere, a contact gets
 * archived) — poll instead of trusting a single snapshot to stay true.
 */
const REPLY_QUEUE_REFETCH_INTERVAL_MS = 60_000;

/** Only what the badge needs to compute a count — no message bodies, no names.
 * `id` is not consumed by the count itself; it's required to anchor the
 * keyset pagination cursor (see `fetchQualifyingDraftRows`). `thread_id` is
 * required by the soft-deleted-thread exclusion (see excludeSoftDeletedThreads) —
 * the badge must agree with the rows list on Trash, not just on live-draft status. */
const REPLY_QUEUE_COUNT_COLUMNS = `
  id,
  heyreach_lead_id,
  incoming_at,
  contact_id,
  thread_id
` as const;

/** Exactly the fields the Reply Queue panel and its fallback row read — nothing else. */
const REPLY_QUEUE_DRAFT_COLUMNS = `
  id,
  thread_id,
  contact_id,
  heyreach_lead_id,
  heyreach_campaign_id,
  lead_name,
  lead_title,
  lead_company,
  incoming_at,
  incoming_message
` as const;

/**
 * Metadata-only: everything a `ReplyQueueDraftRow` already supplies —
 * lead name/company, message preview, timestamp — is deliberately excluded
 * here even though `os_conversation_inbox` carries it too. Fetching it would
 * be a second, redundant message-body load cached under a key that never
 * invalidates when the draft changes (see toDisplayThread in
 * reply-queue-panel.tsx), so it would just go stale for nothing.
 */
const REPLY_QUEUE_THREAD_VIEW_COLUMNS = `
  thread_id,
  current_stage,
  message_count,
  pending_draft_count,
  sender_name
` as const;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function chunkArray<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

interface DraftPageCursor {
  incomingAt: string;
  id: string;
}

/**
 * Reads every qualifying draft row via keyset (cursor) pagination, one page
 * at a time, projecting only `columns`. `id` breaks ties on `incoming_at` so
 * the total order is deterministic — Postgres/PostgREST can order by a
 * column that isn't selected.
 *
 * Deliberately NOT offset pagination (`.range(offset, offset + 499)`): the
 * qualifying set mutates between page requests through ordinary production
 * flows — the webhook supersedes a stale draft and inserts a new
 * `awaiting_draft` row (api/os/webhooks/heyreach.ts), a send flips a draft to
 * `sent` (api/os/linkedin/send.ts) — and that shifts every later row's
 * offset. A concrete case: page one returns R1-R500; R1 is sent before page
 * two requests offset 500; the query for `.range(500, 999)` now returns
 * empty because only 500 rows remain, silently omitting live R501 while the
 * hook still resolves successfully. A keyset cursor anchored to the exact
 * `(incoming_at, id)` pair of the previous page's last row doesn't have this
 * failure mode: rows strictly after that pair in the fixed total order are
 * unaffected by insertions or removals anywhere else in the set.
 */
async function fetchQualifyingDraftRows<T extends ReplyQueueIdentityRow>(
  senderId: ReplyQueueSenderFilter,
  campaignId: string | null,
  organizationId: string,
  columns: string
): Promise<T[]> {
  const rows: T[] = [];
  let cursor: DraftPageCursor | null = null;

  for (;;) {
    let queryBuilder = supabase
      .from('os_linkedin_drafts')
      .select(columns)
      .eq('organization_id', organizationId)
      // `is_superseded` may be NULL for older rows — NULL must count as "not
      // superseded", so this is `.not(..., 'is', true)`, never `.eq(..., false)`.
      .not('is_superseded', 'is', true)
      // Defense-in-depth against schema drift, not a load-bearing predicate:
      // `incoming_message` is `NOT NULL` today
      // (supabase/migrations/001_tacit_initial_schema.sql:261), so this can
      // never currently exclude a row the `.neq()` below wouldn't already
      // exclude in Postgres (`NULL <> ''` is UNKNOWN, filtered). Kept so a
      // future nullable migration can't silently let a null-message row
      // through some other path; covered by a call-shape assertion only —
      // there is no way to build a fixture that would make this predicate's
      // removal observable today.
      .not('incoming_message', 'is', null)
      .neq('incoming_message', '')
      .in('status', REPLY_QUEUE_STATUSES);

    if (senderId === 'unassigned') {
      queryBuilder = queryBuilder.is('sender_id', null);
    } else if (senderId !== 'all') {
      queryBuilder = queryBuilder.eq('sender_id', senderId);
    }

    // ThreadFilters.campaignId maps to os_conversation_threads.heyreach_campaign_id;
    // ReplyQueueFilters.campaignId expresses the same operator intent against the
    // drafts-truth source, os_linkedin_drafts.heyreach_campaign_id.
    if (campaignId !== null) {
      queryBuilder = queryBuilder.eq('heyreach_campaign_id', campaignId);
    }

    if (cursor) {
      queryBuilder = queryBuilder.or(
        `incoming_at.lt."${cursor.incomingAt}",and(incoming_at.eq."${cursor.incomingAt}",id.gt."${cursor.id}")`
      );
    }

    const { data, error } = await queryBuilder
      .order('incoming_at', { ascending: false })
      .order('id', { ascending: true })
      .limit(REPLY_QUEUE_PAGE_SIZE);
    if (error) throw error;

    const page = (data ?? []) as unknown as T[];
    rows.push(...page);

    if (page.length < REPLY_QUEUE_PAGE_SIZE) break;
    const last = page[page.length - 1] as T;
    cursor = { incomingAt: last.incoming_at, id: last.id };
  }

  return rows;
}

/** One row per lead — the winner per the shared draft-recency order
 * (src/lib/crm/live-draft.ts), so a tied `incoming_at` picks the exact same
 * draft the dossier's `livePendingDraft` would pick for the same lead. */
function dedupeByLatestIncoming<T extends ReplyQueueIdentityRow>(rows: T[]): T[] {
  const candidatesByLead = new Map<string, T[]>();
  for (const row of rows) {
    const list = candidatesByLead.get(row.heyreach_lead_id);
    if (list) list.push(row);
    else candidatesByLead.set(row.heyreach_lead_id, [row]);
  }

  const winners: T[] = [];
  for (const candidates of candidatesByLead.values()) {
    const winner = pickMostRecentDraft(candidates);
    if (winner) winners.push(winner);
  }

  return winners.sort(compareDraftRecency);
}

/**
 * Looks up `action_state` for every requested contact id. Fail-closed: under
 * seat-isolation RLS (015_user_seat_isolation.sql), a draft's `user_id` can
 * legally diverge from its contact's `assigned_to` — the webhook preserves
 * an existing contact's owner while a new draft carries the current
 * sender's user_id (api/os/webhooks/heyreach.ts) — so a member can see a
 * draft whose linked contact is RLS-hidden from them. The FK guarantees the
 * contact row exists; if a requested id doesn't come back, that is an
 * unknown/RLS-or-concurrency state, not proof the contact isn't archived.
 * Silently treating it as "not archived" would let a hidden-and-possibly-
 * archived lead through and undercount the hidden total, so this throws
 * instead — the caller's query then reports an honest error, not a number.
 */
async function fetchContactArchivalStatus(
  contactIds: readonly string[],
  organizationId: string
): Promise<Map<string, boolean>> {
  const statusByContact = new Map<string, boolean>();

  for (const chunk of chunkArray(contactIds, LOOKUP_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select('id, action_state')
      .eq('organization_id', organizationId)
      .in('id', chunk);

    if (error) throw error;

    for (const contact of (data ?? []) as Array<{ id: string; action_state: string | null }>) {
      statusByContact.set(contact.id, contact.action_state === 'archived');
    }
  }

  const missing = contactIds.filter((id) => !statusByContact.has(id));
  if (missing.length > 0) {
    throw new Error(
      `Reply Queue: ${missing.length} linked contact(s) did not resolve in the archived-contact lookup — refusing to report an exclusion result that could silently include an RLS-hidden lead.`
    );
  }

  return statusByContact;
}

/**
 * Fail-closed exclusion of drafts whose thread is CONFIRMED Trashed — the
 * same lifecycle contract the rest of the inbox already honors via
 * `os_conversation_inbox`'s `WHERE deleted_at IS NULL`
 * (supabase/migrations/025_soft_delete_threads.sql). The rule itself now
 * lives in `excludeConfirmedTrashedThreads`
 * (src/lib/crm/thread-lifecycle.ts) — shared verbatim with useLeadDossier's
 * winner selection (F4) so the two surfaces can't diverge on what "Trashed"
 * means, or on WHEN it's checked relative to picking a winner, again.
 *
 * `thread_id === null` is a VALID member — there is no thread to have been
 * Trashed. Deliberate, not an oversight: a thread-creation RPC failure is
 * caught and logged WITHOUT aborting the webhook request
 * (api/os/webhooks/heyreach.ts:881-914), and the actionable `awaiting_draft`
 * row is still inserted with a null `thread_id`
 * (api/os/webhooks/heyreach.ts:930-955) — an ordinary transient failure, not
 * a rare edge case. These rows render through the existing fallback-row
 * path (reply-queue-panel.tsx), already built for "thread missing from the
 * view".
 *
 * ACCEPTED RESIDUAL: once the 30-day auto-purge hard-deletes a Trashed
 * thread (`purge_deleted_threads()`, same migration), the FK's
 * `ON DELETE SET NULL` (017_conversation_threads.sql) turns that thread's
 * drafts into `thread_id === null` rows too — indistinguishable, without a
 * migration, from the webhook-failure case above. Such a draft REAPPEARS as
 * a fallback row post-purge: a rare, operator-recoverable "purged ghost"
 * (re-Trash it) beats the common case of a live lead silently hidden behind
 * a confident count.
 *
 * Runs on the fully-paginated candidate set (see `fetchQualifyingDraftRows`,
 * already immune to concurrent mutation via keyset pagination) as one
 * static in-memory filter, not a second paginated query.
 */
async function excludeSoftDeletedThreads<T extends ReplyQueueIdentityRow>(
  rows: T[],
  organizationId: string
): Promise<T[]> {
  return excludeConfirmedTrashedThreads(rows, organizationId);
}

/** Applies the default contact-triage exclusion, or skips it (and its lookup) entirely. */
async function excludeArchivedContacts<T extends ReplyQueueIdentityRow>(
  deduped: T[],
  includeArchived: boolean,
  organizationId: string
): Promise<{ rows: T[]; archivedExcludedCount: number }> {
  if (includeArchived) {
    return { rows: deduped, archivedExcludedCount: 0 };
  }

  const contactIds = Array.from(
    new Set(
      deduped.map((row) => row.contact_id).filter((id): id is string => Boolean(id))
    )
  );

  const statusByContact =
    contactIds.length > 0
      ? await fetchContactArchivalStatus(contactIds, organizationId)
      : new Map<string, boolean>();

  const visibleRows = deduped.filter(
    (row) => !row.contact_id || !statusByContact.get(row.contact_id)
  );

  return { rows: visibleRows, archivedExcludedCount: deduped.length - visibleRows.length };
}

// ─────────────────────────────────────────────────────────────────────────────
// Query-key prefixes — stable, exported so a mutation elsewhere (Trash/
// Restore, F3) can invalidate the exact same cache entries this file reads,
// instead of a hardcoded string literal duplicated in two files that could
// silently drift apart.
// ─────────────────────────────────────────────────────────────────────────────

export const REPLY_QUEUE_COUNT_QUERY_KEY = ['reply-queue-count'] as const;
export const REPLY_QUEUE_ROWS_QUERY_KEY = ['reply-queue'] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Draft Campaign Domain — complete option list + canonical campaign names
// ─────────────────────────────────────────────────────────────────────────────

/** The ONE definition of this cache key. Exported so the rename fan-out test can assert against
 * the key the hook actually uses instead of a hand-copied literal that could drift out from under
 * it while staying green (DIFF r2 F4). */
export const draftCampaignDomainQueryKey = (
  userId: string | null,
  organizationId: string | null
): readonly unknown[] => ['draft-campaign-domain', userId, organizationId];

export const useDraftCampaignDomain = (): ReturnType<typeof useQuery<DraftCampaign[]>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<DraftCampaign[]>({
    queryKey: draftCampaignDomainQueryKey(userId, organizationId),
    queryFn: async () => {
      if (userId === null || organizationId === null) return [];

      // DIFF r2 F2: KEYSET, not offset. An immutable sort key stopped a rename from moving a row
      // across the page boundary, but it did not stabilise the SET: `readAllRows` asks for offset
      // 1000 next, and a webhook inserting the first draft for a new campaign whose id sorts before
      // the boundary shifts every later position by one — so page two repeats the boundary row and
      // skips one never seen, while the short page still reports success. A removal does the mirror
      // image. A cursor anchored to the last id seen is unaffected by inserts or deletes anywhere
      // else, which is exactly why the queue's own row read is keyset (fetchQualifyingDraftRows).
      const campaigns = await readAllRowsKeyset<DraftCampaign, string>(
        () => supabase
          .from('os_campaign_stats')
          .select('heyreach_campaign_id, campaign_name')
          .eq('organization_id', organizationId),
        'heyreach_campaign_id',
        (campaign) => campaign.heyreach_campaign_id,
        (error) => {
          throw error;
        },
        DRAFT_CAMPAIGN_DOMAIN_PAGE_SIZE,
      );

      // Display order is restored here, where no page boundary depends on it.
      return campaigns.sort((left, right) =>
        left.campaign_name.localeCompare(right.campaign_name)
      );
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 30000,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Count Hook — cheap, always mounted, powers the tab badge
// ─────────────────────────────────────────────────────────────────────────────

export const useReplyQueueCount = (
  filters: ReplyQueueFilters
): ReturnType<typeof useQuery<ReplyQueueCountResult>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ReplyQueueCountResult>({
    queryKey: [
      ...REPLY_QUEUE_COUNT_QUERY_KEY,
      userId,
      organizationId,
      filters.senderId,
      filters.campaignId,
      filters.includeArchived,
    ],
    queryFn: async () => {
      if (userId === null || organizationId === null) return { totalCount: 0 };

      const rawRows = await fetchQualifyingDraftRows<ReplyQueueIdentityRow>(
        filters.senderId,
        filters.campaignId,
        organizationId,
        REPLY_QUEUE_COUNT_COLUMNS
      );
      const liveThreadRows = await excludeSoftDeletedThreads(rawRows, organizationId);
      const deduped = dedupeByLatestIncoming(liveThreadRows);
      const { rows } = await excludeArchivedContacts(
        deduped,
        filters.includeArchived,
        organizationId
      );
      return { totalCount: rows.length };
    },
    // The always-mounted badge renders nothing until auth identity resolves,
    // matching the inbox's tenant-scoped query behavior.
    enabled: userId !== null && organizationId !== null,
    staleTime: 10000,
    refetchInterval: REPLY_QUEUE_REFETCH_INTERVAL_MS,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Rows Hook — full content, only needed while the tab is actually open
// ─────────────────────────────────────────────────────────────────────────────

export const useReplyQueue = (
  filters: ReplyQueueFilters,
  options: { enabled: boolean } = { enabled: true }
): ReturnType<typeof useQuery<ReplyQueueResult>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ReplyQueueResult>({
    queryKey: [
      ...REPLY_QUEUE_ROWS_QUERY_KEY,
      userId,
      organizationId,
      filters.senderId,
      filters.campaignId,
      filters.includeArchived,
    ],
    queryFn: async () => {
      if (userId === null || organizationId === null) {
        return { rows: [], archivedExcludedCount: 0 };
      }

      const rawRows = await fetchQualifyingDraftRows<ReplyQueueDraftRow>(
        filters.senderId,
        filters.campaignId,
        organizationId,
        REPLY_QUEUE_DRAFT_COLUMNS
      );
      const liveThreadRows = await excludeSoftDeletedThreads(rawRows, organizationId);
      const deduped = dedupeByLatestIncoming(liveThreadRows);
      const { rows, archivedExcludedCount } = await excludeArchivedContacts(
        deduped,
        filters.includeArchived,
        organizationId
      );

      return { rows, archivedExcludedCount };
    },
    enabled: options.enabled && userId !== null && organizationId !== null,
    staleTime: 10000,
    refetchInterval: REPLY_QUEUE_REFETCH_INTERVAL_MS,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Thread View Lookup — reuses the existing thread list UI for matched rows
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Looks up the `os_conversation_inbox` view rows for a set of thread ids, so
 * Reply Queue rows can be rendered with the existing `ThreadListItem`
 * component instead of a bespoke row type. Queue rows whose thread is
 * missing from the view (deleted, or never materialized) are simply absent
 * from the returned map — callers render a fallback row for those.
 */
export const useReplyQueueThreadViews = (
  threadIds: readonly string[]
): ReturnType<typeof useQuery<Map<string, ReplyQueueThreadMetadata>>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;
  const sortedIds = [...threadIds].sort();

  return useQuery<Map<string, ReplyQueueThreadMetadata>>({
    queryKey: ['reply-queue-thread-views', userId, organizationId, sortedIds],
    queryFn: async () => {
      const map = new Map<string, ReplyQueueThreadMetadata>();
      if (userId === null || organizationId === null) return map;

      for (const chunk of chunkArray(sortedIds, LOOKUP_CHUNK_SIZE)) {
        if (chunk.length === 0) continue;

        const { data, error } = await supabase
          .from('os_conversation_inbox')
          .select(REPLY_QUEUE_THREAD_VIEW_COLUMNS)
          .eq('organization_id', organizationId)
          .in('thread_id', chunk);

        if (error) throw error;

        for (const thread of (data ?? []) as unknown as ReplyQueueThreadMetadata[]) {
          map.set(thread.thread_id, thread);
        }
      }

      return map;
    },
    enabled: sortedIds.length > 0 && userId !== null && organizationId !== null,
    staleTime: 10000,
  });
};
